import { FieldError } from 'react-hook-form';
import CloseIcon from '@mui/icons-material/Close';
import React, { useEffect, useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import type { TaskState, UploadTask } from '@firebase/storage';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Stack, Typography, Button, LinearProgress, Collapse, Box } from '@mui/material';

import { formatBytes } from './utils';

const determineShouldShowProgress = (state: TaskState | undefined) => state === 'running' || state === 'paused';

type ActionButtonProps = {
  state: TaskState | undefined;
  onRemove: () => void;
  uploadTask: UploadTask | undefined;
  isUploading?: boolean;
};

const ActionButton = ({ state, onRemove, uploadTask, isUploading }: Readonly<ActionButtonProps>) => {
  const handleCancel = () => {
    uploadTask?.cancel();
    onRemove();
  };

  if (state === 'running') {
    return (
      <Button
        size="small"
        sx={{
          width: 16,
          height: 16,
          padding: 0,
          minWidth: 16,
        }}
        color="secondary"
        onClick={handleCancel}
      >
        <CloseIcon sx={{ width: 16, height: 16 }} />
      </Button>
    );
  }

  if (state === 'success') {
    return <CheckCircleIcon color="success" sx={{ width: 16, height: 16 }} />;
  }

  return (
    <Button
      size="small"
      sx={{
        width: 16,
        height: 16,
        padding: 0,
        minWidth: 16,
      }}
      onClick={onRemove}
      disabled={isUploading}
    >
      <DeleteOutlinedIcon sx={{ width: 16, height: 16 }} />
    </Button>
  );
};

type FileItemProps = {
  item: { file: File; uploadTask: UploadTask | undefined; id: string };
  onRemove: () => void;
  isUploading?: boolean;
  error?: FieldError;
};

export const FileItem: React.FC<FileItemProps> = ({ item, onRemove, error, isUploading = false }) => {
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState<TaskState | undefined>(undefined);

  const uploadTask = useMemo(() => item.uploadTask, [item.uploadTask]);

  useEffect(() => {
    if (!uploadTask) {
      setProgress(0);
      setState(undefined);

      return () => {};
    }

    const unsubscribe = uploadTask.on('state_changed', {
      next: (snapshot) => {
        setProgress(Math.min(snapshot.bytesTransferred / snapshot.totalBytes, 1) * 100);
        setState(snapshot.state);
      },
      error: () => {
        setProgress(0);
        setState(undefined);
      },
      complete: () => {
        setProgress(100);
        setState('success');
      },
    });

    return () => {
      unsubscribe();
    };
  }, [uploadTask]);

  const shouldShowProgress = determineShouldShowProgress(state);

  return (
    <Stack direction="column" maxWidth="100%">
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
        sx={{ width: '100%', flexGrow: 1 }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flex={1}
          width="100%"
          sx={{ opacity: isUploading ? 0.5 : 1, minWidth: 0 }}
        >
          <Stack direction="column" sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {item.file.name}
            </Typography>
            {!!error && (
              <Typography variant="caption" color="secondary">
                {error.message}
              </Typography>
            )}
          </Stack>
          <Typography variant="body2" fontSize={10} sx={{ flexShrink: 0, ml: 1 }}>
            {formatBytes(item.file.size)}
          </Typography>
        </Stack>

        <ActionButton key={state} state={state} onRemove={onRemove} uploadTask={uploadTask} isUploading={isUploading} />
      </Stack>
      <Collapse in={shouldShowProgress}>
        <Box width="100%" mt={0.5} overflow="hidden">
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      </Collapse>
    </Stack>
  );
};
